/**
 * NA-313: field-level "Edited" badge plus a revert action, for a Question
 * Bank-owned field that one lesson block has taken ownership of.
 *
 * Mirrors the badge/link shape of `ContentBlockWrapper`'s whole-block
 * "Customized" bar so the two read as the same idea at different granularity —
 * but deliberately skips its confirmation dialog: discarding one field's
 * override is far lower-stakes than disconnecting an entire block, and the
 * ticket calls for immediate revert.
 *
 * The two indicators are independent and can appear together: a block can be
 * customized for a client account (block vs Emtrain) while this field also
 * diverges (field vs Question Bank).
 *
 * Divergence is COMPUTED by comparing values, not read from the ownership
 * marker — so a block overridden with text identical to the bank shows nothing,
 * and starts showing "Edited" if the bank value later changes. Callers pass
 * `isDiverged`; see `useFieldOverrideState`.
 */

import { useMemo } from 'react';
import type { CSSProperties } from 'react';
import { useTheme } from '@course-authoring/shared-types';

export interface FieldOverrideStatusProps {
  /** True when the block's value differs from the bank value for the viewed locale. */
  isDiverged: boolean;
  /** Discards the block-level value. Omit to show the badge with no action. */
  onRevert?: () => void;
  /** Names the field in the revert control's accessible label. */
  fieldLabel: string;
}

export function FieldOverrideStatus({
  isDiverged,
  onRevert,
  fieldLabel,
}: FieldOverrideStatusProps): React.ReactElement | null {
  const { tokens } = useTheme();
  const styles = useMemo(() => createStyles(tokens), [tokens]);

  if (!isDiverged) return null;

  return (
    <span style={styles.container}>
      <span style={styles.badge}>Edited</span>
      {onRevert && (
        <button
          type="button"
          onClick={onRevert}
          style={styles.revertLink}
          aria-label={`Revert ${fieldLabel} to Question Bank value`}
        >
          Revert to Question Bank value
        </button>
      )}
    </span>
  );
}

FieldOverrideStatus.displayName = 'FieldOverrideStatus';

function createStyles(
  tokens: ReturnType<typeof useTheme>['tokens'],
): Record<string, CSSProperties> {
  return {
    container: {
      display: 'inline-flex',
      alignItems: 'baseline',
      gap: tokens.spacing.sm,
      textTransform: 'none',
      letterSpacing: 'normal',
    },
    // Same pill as ContentBlockWrapper's "Customized" badge.
    badge: {
      display: 'inline-flex',
      alignItems: 'center',
      padding: `2px ${tokens.spacing.sm}`,
      borderRadius: tokens.radius.full,
      border: `1px solid ${tokens.banner.info.border}`,
      backgroundColor: tokens.backgrounds.surface,
      color: tokens.banner.info.border,
      fontSize: '12px',
      fontWeight: 700,
      whiteSpace: 'nowrap',
    },
    revertLink: {
      background: 'none',
      border: 'none',
      padding: 0,
      color: tokens.text.link,
      fontSize: '13px',
      fontWeight: 600,
      textDecoration: 'underline',
      cursor: 'pointer',
      whiteSpace: 'nowrap',
    },
  };
}
